/**
 * taskflow-schema.ts
 *
 * Authoritative 5-Tier Polymorphic Domain Schema for TaskFlow.
 * Supports Goal -> Plan -> Project -> Task -> Action with flexible,
 * shallow nesting, rich frontmatter, and relationship graphs.
 */

import { z } from 'zod';

export const TASKFLOW_ITEM_TYPES = ['goal', 'plan', 'project', 'task', 'action'] as const;
export type TaskFlowItemType = (typeof TASKFLOW_ITEM_TYPES)[number];

export const TASKFLOW_STATUSES = [
  'backlog',
  'active',
  'in-progress',
  'blocked',
  'review',
  'done',
  'cancelled',
  'archived',
] as const;
export type TaskFlowStatus = (typeof TASKFLOW_STATUSES)[number] | string;

export const TASKFLOW_PRIORITIES = ['low', 'normal', 'medium', 'high', 'urgent'] as const;
export type TaskFlowPriority = (typeof TASKFLOW_PRIORITIES)[number];

export const TASKFLOW_RELATIONSHIP_TYPES = [
  'blockedBy',
  'dependsOn',
  'relatesTo',
  'duplicates',
  'parent',
  'child',
] as const;
export type TaskFlowRelationshipType = (typeof TASKFLOW_RELATIONSHIP_TYPES)[number];

export const TaskFlowRelationshipSchema = z.object({
  type: z.enum(TASKFLOW_RELATIONSHIP_TYPES),
  targetId: z.string().min(1, 'Target ID is required'),
  reason: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
});
export type TaskFlowRelationship = z.infer<typeof TaskFlowRelationshipSchema>;

// Strict date string validator (YYYY-MM-DD or ISO 8601 with optional offset)
const dateOrTimestampRegex =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

const DateStringSchema = z
  .string()
  .refine((val) => dateOrTimestampRegex.test(val), {
    message: 'Must be in YYYY-MM-DD or ISO-8601 UTC/Offset format',
  });

/**
 * Base TaskFlow Properties common to all 5 note types
 */
const BaseTaskFlowProperties = {
  id: z.string().min(1, 'Task ID is required'),
  title: z.string(),
  description: z.string().nullable().optional(),
  status: z.string().min(1, 'Status is required'),
  priority: z.enum(TASKFLOW_PRIORITIES).optional().default('normal'),
  startDate: DateStringSchema.optional(),
  endDate: DateStringSchema.optional(),
  dueDate: DateStringSchema.optional(),
  scheduledDate: DateStringSchema.optional(),
  completedDate: DateStringSchema.optional(),
  progressPercentage: z
    .number()
    .min(0, 'Progress must be >= 0')
    .max(100, 'Progress must be <= 100')
    .optional()
    .default(0),
  parent: z.string().nullable().optional(),
  tags: z.array(z.string()).optional().default([]),
  assigneeIds: z.array(z.string()).optional().default([]),
  estimateHours: z.number().nonnegative('Estimate hours must be non-negative').optional(),
  actualHours: z.number().nonnegative('Actual hours must be non-negative').optional(),
  customProperties: z.record(z.string(), z.unknown()).optional().default({}),
  relationships: z.array(TaskFlowRelationshipSchema).optional().default([]),
  fractionalIndex: z.string().optional(),
  revision: z.number().int().positive().optional().default(1),
  contentHash: z.string().optional(),
  eventHash: z.string().optional(),
  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
};

/**
 * Discriminated Union schemas for the 5 Note Types
 */
export const GoalItemSchema = z.object({
  ...BaseTaskFlowProperties,
  type: z.literal('goal'),
});

export const PlanItemSchema = z.object({
  ...BaseTaskFlowProperties,
  type: z.literal('plan'),
});

export const ProjectItemSchema = z.object({
  ...BaseTaskFlowProperties,
  type: z.literal('project'),
});

export const TaskItemSchema = z.object({
  ...BaseTaskFlowProperties,
  type: z.literal('task'),
});

export const ActionItemSchema = z.object({
  ...BaseTaskFlowProperties,
  type: z.literal('action'),
});

export const TaskFlowItemSchema = z.discriminatedUnion('type', [
  GoalItemSchema,
  PlanItemSchema,
  ProjectItemSchema,
  TaskItemSchema,
  ActionItemSchema,
]);

export type TaskFlowItem = z.infer<typeof TaskFlowItemSchema>;

export const TaskFlowFrontmatterSchema = z.object({
  ...BaseTaskFlowProperties,
  type: z.enum(TASKFLOW_ITEM_TYPES),
});

export type TaskFlowFrontmatter = z.infer<typeof TaskFlowFrontmatterSchema>;

export type TaskFlowValidationResult =
  | { success: true; data: TaskFlowItem }
  | { success: false; error: z.ZodError };

/**
 * Helper to validate raw object into typed TaskFlowItem
 */
export function validateTaskFlowItem(raw: unknown): TaskFlowValidationResult {
  const result = TaskFlowItemSchema.safeParse(raw);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

/**
 * Factory helper for creating new TaskFlow Items
 */
export function createTaskFlowItem<T extends TaskFlowItemType>(
  type: T,
  params: Partial<Omit<TaskFlowItem, 'type'>> & { title: string; status?: string }
): TaskFlowItem {
  const now = Date.now();
  const id = params.id || `${type}-${now}-${Math.random().toString(36).slice(2, 7)}`;

  const item = {
    id,
    type,
    title: params.title,
    description: params.description ?? '',
    status: params.status || 'backlog',
    priority: params.priority || 'normal',
    progressPercentage: params.progressPercentage ?? 0,
    tags: params.tags || [],
    assigneeIds: params.assigneeIds || [],
    customProperties: params.customProperties || {},
    relationships: params.relationships || [],
    revision: 1,
    createdAt: now,
    updatedAt: now,
    ...params,
  };

  const validated = TaskFlowItemSchema.parse(item);
  return validated;
}
